import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, MoreHorizontal, Search, Settings, Star, X } from 'lucide-react';
import { SearchDropdown } from '@/components/ui/SearchDropdown';
import { Avatar } from '@/components/ui/Avatar';
import { ManageMembersModal } from '@/components/modals/ManageMembersModal';
import { EditProjectModal } from '@/components/modals/EditProjectModal';
import { orgOf } from '@/lib/project-groups';
import { projectsApi } from '@/lib/api';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { useCanFavorite } from '@/hooks/useCanFavorite';
import { cn } from '@/lib/utils';

/**
 * A project's header, drawn IN the app's topbar (AppShell's `topbar` slot)
 * rather than as a second bar beneath it. The topbar used to say "Projects"
 * over a header that named the project — two bars, one of them saying nothing
 * (Jesse, 2026-11-04). The hub-wide search went with it; this project's
 * search is the one that stays.
 *
 * Org and name, favorite, settings, search, members. Switching projects
 * happens in the nav's dropdowns.
 */
export function ProjectTopbar({ projectId }: { projectId: string }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  /** On a phone the search is an icon until asked for, then takes the row. */
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const canFavorite = useCanFavorite();
  const { isProjectAdmin: isAgencyAdmin } = useProjectRoles(projectId);
  const queryClient = useQueryClient();

  // Same key as ProjectLayout, so this is one request, not two.
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
  });

  const favoriteMutation = useMutation({
    mutationFn: (isFavorited: boolean) =>
      isFavorited ? projectsApi.unfavorite(projectId) : projectsApi.favorite(projectId),
    onMutate: async (isFavorited: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['project', projectId] });
      const previous = queryClient.getQueryData<any>(['project', projectId]);
      if (previous) {
        queryClient.setQueryData(['project', projectId], { ...previous, isFavorited: !isFavorited });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['project', projectId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const isArchived = !!project?.archivedAt;
  const memberCount = project?.members?.length || 0;
  const orgName = project ? orgOf(project).name : '';

  const closeSearch = () => {
    setSearchQuery('');
    setSearchOpen(false);
    setMobileSearchOpen(false);
  };

  return (
    <div className="flex w-full min-w-0 items-center gap-2 sm:gap-3">
      <div className={cn('flex min-w-0 items-center gap-2 sm:gap-3', mobileSearchOpen && 'hidden md:flex')}>
        <div className="min-w-0">
          {orgName && (
            <p className="truncate text-caption font-semibold uppercase tracking-wider text-grey-400">{orgName}</p>
          )}
          <h1 className="truncate text-heading-4 font-black leading-tight text-secondary">
            {project?.name || (isLoading ? 'Loading...' : 'Project Not Found')}
          </h1>
        </div>
        {project?.isPrivate && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-grey-200 bg-grey-100 px-2.5 py-0.5 text-xs font-medium text-grey-600">
            <Lock className="h-3 w-3" />
            Private
          </span>
        )}
        {isArchived && <span className="badge badge-grey shrink-0 text-xs">Archived</span>}
        {/* Favorite toggle — agency people only (useCanFavorite) */}
        {project && canFavorite && (
          <button
            type="button"
            onClick={() => favoriteMutation.mutate(!!project.isFavorited)}
            className={cn(
              'shrink-0 rounded-lg p-1.5 transition-colors',
              project.isFavorited
                ? 'text-warning hover:bg-grey-100'
                : 'text-grey-400 hover:bg-grey-100 hover:text-warning',
            )}
            title={project.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={project.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className={cn('h-4 w-4', project.isFavorited && 'fill-yellow-400')} />
          </button>
        )}
        {isAgencyAdmin && project && (
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="shrink-0 rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
            title="Project settings"
            aria-label="Project settings"
          >
            <Settings className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Project search. Desktop: always a box. Phone: an icon that gives
          the search the whole row, and gives it back on close. */}
      <div className={cn('relative ml-auto', mobileSearchOpen ? 'w-full md:w-72' : 'md:w-72')}>
        {!mobileSearchOpen && (
          <button
            type="button"
            onClick={() => setMobileSearchOpen(true)}
            aria-label={`Search ${project?.name ?? 'this project'}`}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary md:hidden"
          >
            <Search className="h-5 w-5" />
          </button>
        )}
        <div className={cn('group relative', !mobileSearchOpen && 'hidden md:block')}>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-400 transition-colors group-focus-within:text-grey-600" />
          <input
            type="search"
            aria-label={`Search ${project?.name ?? 'this project'}`}
            placeholder="Search this project…"
            autoFocus={mobileSearchOpen}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchOpen(e.target.value.length >= 2);
            }}
            onFocus={() => {
              if (searchQuery.length >= 2) setSearchOpen(true);
            }}
            className="h-9 w-full rounded-lg border-0 bg-grey-100 pl-9 pr-9 text-sm transition-all placeholder:text-grey-400 hover:bg-grey-150 focus:bg-white focus:ring-2 focus:ring-grey-200"
          />
          {(searchQuery || mobileSearchOpen) && (
            <button
              type="button"
              onClick={closeSearch}
              aria-label={searchQuery ? 'Clear search' : 'Close search'}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-grey-400 transition-colors hover:bg-grey-200 hover:text-grey-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <SearchDropdown
          query={searchQuery}
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
          projectId={projectId}
          organizationId={project?.organizationId}
        />
      </div>

      {/* Members — tighter overlap on a phone */}
      <div className={cn('flex shrink-0 items-center', mobileSearchOpen && 'hidden md:flex')}>
        <div className="hidden -space-x-2 sm:flex">
          {project?.members?.slice(0, 4).map((member: any) => (
            <Avatar
              key={member.userId}
              src={member.user?.avatarUrl}
              name={member.user?.name || 'User'}
              size="sm"
              className="ring-2 ring-white"
            />
          ))}
        </div>
        {memberCount > 4 && (
          <div className="-ml-2 hidden h-8 w-8 items-center justify-center rounded-full bg-grey-200 text-xs font-medium text-grey-600 ring-2 ring-white sm:flex">
            +{memberCount - 4}
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowManageMembers(true)}
          className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-grey-100 ring-2 ring-white transition-colors hover:bg-grey-200"
          title={isAgencyAdmin ? 'Manage members' : 'View members'}
          aria-label={isAgencyAdmin ? 'Manage members' : 'View members'}
        >
          <MoreHorizontal className="h-4 w-4 text-grey-600" />
        </button>
      </div>

      {/* Portalled: the topbar's backdrop-blur makes it the containing block
          for anything `fixed` inside it, so an inline modal would be laid out
          inside a 64px bar. */}
      {project &&
        createPortal(
          <>
            <ManageMembersModal
              isOpen={showManageMembers}
              onClose={() => setShowManageMembers(false)}
              projectId={projectId}
              project={project}
              readOnly={!isAgencyAdmin}
            />
            <EditProjectModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} project={project} />
          </>,
          document.body,
        )}
    </div>
  );
}
